import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import Grid from '@/global/components/ui/Grid'
import { useAuth } from '@/hooks'
import { hasPermission } from '@/hooks/useBackendMenu'
import { CategoriaEnum } from '@acme/auth'
import dayjs from 'dayjs'
import {
    FormDatePicker,
    FormField,
    FormFieldSelect,
    FormFile,
} from 'global/components/Form'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import {
    AfastamentoV2,
    ocorrencia,
    ocorrenciaTipoToPermission,
} from 'service/api'
import TabFuncionariosForm from '../atoms/TabsFuncionarios'
import { funcionarioPodeSolicitarAjustes } from '../modals/DetalhesRegistros'

interface IProps {
    funcionarioId?: number
    setIds?: (ids: number[]) => void
    /** Renderiza o seletor de empresa dentro da seção "Funcionários". */
    selectEmpresa?: boolean
}

export default function FormNovoAfastamento({
    funcionarioId,
    setIds,
    selectEmpresa,
}: IProps) {
    const { data: ocorrencias = [] } = ocorrencia.useFetch({
        refetchOnWindowFocus: false,
    })
    const { watch, setValue } = useFormContext<
        AfastamentoV2 & { dias: number }
    >()
    const {
        decoded: { categoria },
    } = useAuth()
    const [dataInicial, dataFinal, dias] = watch([
        'dataInicial',
        'dataFinal',
        'dias',
    ])

    const isDiasFocado = document.activeElement?.id === 'field-dias'
    const isDataFocado = ['field-data-final', 'field-data-inicial'].includes(
        document.activeElement?.id ?? '',
    )

    useEffect(() => {
        if (isDiasFocado) {
            if (!dataInicial || dias < 1) return
            const dataFinal = dayjs(dataInicial)
                .add(dias - 1, 'day')
                .format('YYYY-MM-DD')
            setValue('dataFinal', dataFinal as any)
        }
        if (isDataFocado) {
            if (!dataInicial || dayjs(dataFinal).isBefore(dataInicial)) return
            setValue('dias', dayjs(dataFinal).diff(dataInicial, 'day') + 1)
        }
    }, [dataInicial, dataFinal, isDiasFocado, isDataFocado, setValue, dias])

    const funcionarioPodeAjustar = funcionarioPodeSolicitarAjustes()

    const MemoizedFuncionarios = useMemo(
        () => (
            <TabFuncionariosForm
                funcionarioIds={funcionarioId ? [funcionarioId] : []}
                onChange={
                    setIds
                        ? setIds
                        : () => {
                              void 0
                          }
                }
                collapsible
                selectEmpresa={selectEmpresa}
                key="tab"
            />
        ),
        [funcionarioId, setIds, selectEmpresa],
    )

    return [
        MemoizedFuncionarios,
        <Grid container key="grid">
            <Grid item xs={12}>
                <HorizontalDivider className="mb-2">
                    Afastamento
                </HorizontalDivider>
            </Grid>
            <Grid item xs={12}>
                <FormFieldSelect
                    name="ocorrenciaId"
                    label="Ocorrência"
                    required
                    options={ocorrencias
                        .filter(
                            it =>
                                funcionarioPodeAjustar ||
                                hasPermission([
                                    'create',
                                    ocorrenciaTipoToPermission(it.tipo),
                                ]),
                        )
                        .filter(
                            categoria === CategoriaEnum.Supervisor
                                ? it => !it.disabled
                                : () => true,
                        )
                        .map(({ codigo, id, descricao, tipo }) => ({
                            value: id,
                            label: `${String(codigo).padStart(
                                3,
                                '0',
                            )}: ${descricao}`,
                            disabled:
                                !funcionarioPodeAjustar &&
                                !hasPermission([
                                    'create',
                                    ocorrenciaTipoToPermission(tipo),
                                ]),
                        }))}
                />
            </Grid>
            <Grid item xs={5}>
                <FormDatePicker
                    id="field-data-inicial"
                    name="dataInicial"
                    label="Data inicial"
                    type="date"
                    required
                />
            </Grid>
            <Grid item xs={5}>
                <FormDatePicker
                    required
                    id="field-data-final"
                    name="dataFinal"
                    label="Data final"
                    type="date"
                />
            </Grid>
            <Grid item xs={2}>
                <FormField
                    id="field-dias"
                    name="dias"
                    label="Dias"
                    type="number"
                    disabled={!dataInicial}
                />
            </Grid>
            <Grid item xs={12}>
                <FormField
                    name="justificativa"
                    label="Justificativa"
                    multiline
                    max={190}
                />
            </Grid>
            <Grid item xs={12}>
                <FormFile name="fileUrl" />
            </Grid>
        </Grid>,
    ]
}
